const ALIGN = /\bql-align-(center|right|justify)\b/;
const INDENT = /\bql-indent-(\d+)\b/;
const SIZE = /\bql-size-(small|large|huge)\b/;
const FONT = /\bql-font-([\w-]+)\b/;

// Quill renders its `size` and `font` formats as classes and relies on
// quill.snow.css for the actual values. That stylesheet is going away with the
// editor, so the values are inlined here (copied from quill 1.3 snow) to keep
// legacy swipes looking the way their author left them.
const SIZES = {
  small: '0.75em',
  large: '1.5em',
  huge: '2.5em',
};

const FONTS = {
  serif: 'Georgia, "Times New Roman", serif',
  monospace: 'Monaco, "Courier New", monospace',
};

function mergeStyle(existing, extra) {
  const base = (existing || '').trim().replace(/;?$/, '');
  return base ? `${base};${extra}` : extra;
}

export function normaliseQuillHtml(html) {
  if (!html || html.indexOf('ql-') === -1) return html || '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  doc.body.querySelectorAll('[class*="ql-"]').forEach((el) => {
    const classes = el.getAttribute('class') || '';
    const align = classes.match(ALIGN);
    const indent = classes.match(INDENT);
    const size = classes.match(SIZE);
    const font = classes.match(FONT);
    let style = el.getAttribute('style') || '';
    if (align) style = mergeStyle(style, `text-align:${align[1]}`);
    if (indent) style = mergeStyle(style, `margin-left:${Number(indent[1]) * 2}em`);
    if (size && SIZES[size[1]]) style = mergeStyle(style, `font-size:${SIZES[size[1]]}`);
    if (font && FONTS[font[1]]) style = mergeStyle(style, `font-family:${FONTS[font[1]]}`);
    const kept = classes.split(/\s+/).filter((c) => c && !c.startsWith('ql-')).join(' ');
    if (kept) el.setAttribute('class', kept);
    else el.removeAttribute('class');
    if (style) el.setAttribute('style', style);
  });
  return doc.body.innerHTML;
}

/**
 * Outbound counterpart to `normaliseQuillHtml`.
 *
 * The schema models a list item as `paragraph block*`, so ProseMirror always
 * writes `<li><p>text</p></li>` where the stored promo asset (and every legacy
 * Quill one) has `<li>text</li>`. That extra paragraph changes how the item
 * renders wherever the HTML is replayed - affiliate portal, emails - so it is
 * unwrapped on the way out.
 *
 * Only a paragraph that is the *sole* child of the item and carries no
 * attributes of its own is unwrapped: a nested list beside it, a second
 * paragraph, or an aligned/styled paragraph all keep their wrapper because
 * removing it would lose structure or formatting.
 */
export function unwrapListItemParagraphs(html) {
  if (!html || html.indexOf('<li') === -1) return html || '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  doc.body.querySelectorAll('li').forEach((item) => {
    const paragraph = item.children.length === 1 && item.children[0].tagName === 'P'
      ? item.children[0]
      : null;
    if (!paragraph || paragraph.attributes.length) return;

    const onlyChild = Array.prototype.every.call(
      item.childNodes,
      node => node === paragraph || (node.nodeType === 3 && !node.nodeValue.trim())
    );
    if (!onlyChild) return;

    while (paragraph.firstChild) item.insertBefore(paragraph.firstChild, paragraph);
    item.removeChild(paragraph);
  });

  return doc.body.innerHTML;
}
